import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useBackendFlags, type CallBackendName } from "@/hooks/useBackendFlags";

interface BackendPickerProps {
  value: CallBackendName;
  onChange: (backend: CallBackendName) => void;
  disabled?: boolean;
}

/**
 * The V1 / V2 choice for a voice call, showing only the backends the organization has.
 * A value the organization does not have is switched to its default.
 */
export function BackendPicker({ value, onChange, disabled }: BackendPickerProps) {
  const { voiceBackends, defaultBackend } = useBackendFlags();

  useEffect(() => {
    if (voiceBackends.length > 0 && !voiceBackends.includes(value)) onChange(defaultBackend);
  }, [value, voiceBackends.join(","), defaultBackend, onChange]);

  if (voiceBackends.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Voice calls are not enabled for this organization.
      </p>
    );
  }

  return (
    <div className={`grid gap-2 ${voiceBackends.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
      {voiceBackends.map((backend) => (
        <Button
          key={backend}
          type="button"
          variant={value === backend ? "default" : "outline"}
          onClick={() => onChange(backend)}
          disabled={disabled}
        >
          {backend.toUpperCase()}
        </Button>
      ))}
    </div>
  );
}
